import { useEffect, useMemo, useState } from "react";

// Custom hook to filter data based on provided filters
const useFilterData = ({
  initialData,
  initialFilter,
  checkers,
  initialDataLoading,
  onChangeFilter,
}) => {
  const [filter, setFilter] = useState(initialFilter);

  // Function to filter data based on provided filters
  const handleGetFilteredData = (filters, data) => {
    // Check if item passes all filters
    const filterChecker = (item) =>
      filters?.every((filter) =>
        (filter?.value && filter?.value?.length) ||
        Object.keys(filter?.value).length
          ? filter.checker(filter.value, item)
          : true
      );

    return data?.filter(filterChecker);
  };

  // Memoized version of filtered data
  let getFilteredData = useMemo(() => {
    onChangeFilter?.();
    return handleGetFilteredData(
      checkers?.map((item) => ({ ...item, value: filter?.[item?.key] })),
      initialData
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, initialData]);

  // Function to change filter
  const handleChangeFilter = (_filter, multiple = false) => {
    // If multiple, add to existing filter
    if (multiple) {
      const array = filter[Object.keys(_filter)[0]];
      array.push(_filter[Object.keys(_filter)[0]]);
      return setFilter((prev) => ({
        ...prev,
        [Object.keys(_filter)[0]]: array.filter(
          (value, index, self) => self.indexOf(value) === index
        ),
      }));
    }
    // If not multiple, replace existing filter
    setFilter((prev) => ({ ...prev, ..._filter }));
  };

  // Function to remove filter
  const handleRemoveFilter = (_filter) => {
    // Remove filter based on its type
    const key = Object.keys(_filter)[0];
    let filterClone = filter;
    switch (true) {
      case Array.isArray(filterClone[key]):
        filterClone[key] = filterClone[key].filter(
          (item) => item !== _filter[key]
        );
        break;
      case typeof filterClone[key] === "object":
        filterClone[key] = {};
        break;
      case typeof filterClone[key] === "boolean":
        filterClone[key] = false;
        break;
      default:
        filterClone[key] = "";
    }
    setFilter((prev) => ({ ...prev, ...filterClone }));
  };

  const [filteredData, setFilteredData] = useState(getFilteredData);

  // Update filtered data when initial data or filters changes
  useEffect(() => {
    if (!initialDataLoading) {
      setFilteredData(getFilteredData);
    }
  }, [initialDataLoading, getFilteredData]);

  return {
    filteredData,
    setFilteredData,
    getFilteredData,
    filter,
    setFilter,
    handleChangeFilter,
    handleRemoveFilter,
  };
};

export default useFilterData;
